import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button, Loader, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import TextAreaField from 'components/TextAreaField';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import validations from 'validations';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import EmailField from 'components/EmailField';

import './InvestorVerifyFormModal.styles.scss';
import './InvestorVerifyFormModal.styles.responsive.scss';

type InvestorVerifyFormModalProps = {
  className?: string;
};

export type InvestorVerifyForm = {
  firstName: string;
  lastName: string;
  email: string;
  briefNote?: string;
};

const InvestorVerifyFormModal: ForwardRefRenderFunction<
  OverlayRef,
  InvestorVerifyFormModalProps
> = (props, ref) => {
  const { className } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const { updateCurrentUser } = useContext(CurrentUserContext);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('investor-verify-form-modal', className);

  const format = useCallback((value: string) => {
    if (!value) return '';
    return value.trim();
  }, []);

  const handleOnSubmit = useCallback(
    async (values: InvestorVerifyForm) => {
      try {
        const { data } = await api.accountants.sendForm(values);
        updateCurrentUser({ accountant: data });
        modal.close();
      } catch (error) {
        popServerError(error);
      }
    },
    [modal, popServerError, updateCurrentUser],
  );

  const { t } = useTranslation();

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('investorVerifyFormTitle')}
    >
      <Form
        onSubmit={handleOnSubmit}
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          briefNote: '',
        }}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            {submitting && <Loader />}
            <div className="name">
              <Field
                name="firstName"
                label={t('accountantFirstName')}
                component={InputField}
                validate={validations.required()}
                required
                format={format}
                formatOnBlur
              />
              <Field
                name="lastName"
                label={t('accountantLastName')}
                component={InputField}
                validate={validations.required()}
                required
                format={format}
                formatOnBlur
              />
            </div>
            <EmailField
              label={t('accountantEmail')}
              required
              format={format}
              formatOnBlur
            />
            <Field
              name="briefNote"
              label={t('includeBriefNote')}
              component={TextAreaField}
              maxLength={500}
              format={format}
              formatOnBlur
              validate={validations.required()}
              required
            />
            <Button disabled={submitting} type="submit">
              {t('sendForm')}
            </Button>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(InvestorVerifyFormModal);
